import { useState } from "react";
import { addRecord, D, removeRecord, reviseRecord, NOT_APPLICABLE, type Decision, type ExpenseTreatment, type Issue, type Source, type YearFile } from "@ftr/tax-core";
import { Card } from "../components/ui.tsx";
import { newId, nowIso } from "../lib/yearStore.ts";
import { useOrganizer, type StepId } from "./context.tsx";
import { personName } from "./common.tsx";
import { fmtUsd } from "./FuturesStep.tsx";
import { TextInput } from "./fields.tsx";

const HOUSEHOLD = ["NO_TAXPAYER", "FILING_STATUS_NEEDED", "SPOUSE_NEEDED", "SPOUSE_STATUS_MISMATCH", "SCREENING_NEEDED", "UNSUPPORTED_SITUATION", "SS_WAGES_NEEDED", "OTHER_SE_NEEDED"];

/** Which step holds the records an issue is about. */
export function stepFor(i: Issue): StepId {
  if (HOUSEHOLD.includes(i.code)) return "you";
  if (i.module === "section1256") return "futures";
  if (i.module === "propBusiness" || i.code === "SE_WAITS_ON_PROP" || i.code === "SE_PROVISIONAL") return "prop";
  return "review";
}

const STEP_NAME: Record<StepId, string> = { you: "You", futures: "Futures", prop: "Prop firms", review: "Review", report: "Report" };

const rev = (list: { id: string; revision: number; deletedAt: string | null }[], id: string) => list.find((r) => r.id === id && r.deletedAt === null)?.revision;
const sourceRev = (y: YearFile, id: string) => rev(y.sources, id) ?? 0;
const eventRev = (y: YearFile, id: string) => rev(y.events, id) ?? 0;

function formLabel(s: Source | undefined): string {
  if (!s) return "a removed form";
  const kind = s.kind === "form_1099b" ? "1099-B" : s.kind === "form_1099nec" ? "1099-NEC" : s.kind === "form_1099misc" ? "1099-MISC" : s.kind === "form_1099k" ? "1099-K" : "record";
  const issuer = "issuer" in s ? s.issuer : "";
  const corrected = "correctedAsPrinted" in s && s.correctedAsPrinted ? " (CORRECTED)" : "";
  const desc = s.kind === "form_1099b" && s.description ? `, ${s.description}` : "";
  return `${issuer || "Unnamed"} ${kind}${corrected}${desc}`;
}

/** A form's name plus its main figure, so two similar forms can be told apart. */
function describe(s: Source): string {
  const amount = "incomeBox" in s && s.incomeBox.state === "value" ? fmtUsd(s.incomeBox.value) : s.kind === "form_1099b" && s.box11.state === "value" ? `box 11 ${fmtUsd(s.box11.value)}` : null;
  return amount ? `${formLabel(s)} (${amount})` : formLabel(s);
}

/** Replace any earlier decision of the same kind about the same thing, so only one answer is ever live. */
type NewDecision = Decision extends infer R ? (R extends unknown ? Omit<R, "revision" | "deletedAt"> : never) : never;

function decide(y: YearFile, d: NewDecision, sameAs: (x: Decision) => boolean): YearFile {
  let next = y;
  for (const old of y.decisions) if (old.deletedAt === null && old.kind === d.kind && sameAs(old)) next = removeRecord(next, "decisions", old.id, nowIso());
  return addRecord(next, "decisions", d);
}

export function ReviewStep() {
  const { analysis, go } = useOrganizer();
  const blockers = analysis.issues.filter((i) => i.severity === "blocker");
  const reviews = analysis.issues.filter((i) => i.severity === "review");
  const infos = analysis.issues.filter((i) => i.severity === "info");

  return (
    <div className="steps-body">
      <Card title="Check before you file">
        <p className="card-intro">
          Every figure waits until the facts behind it are confirmed. Items marked <strong>needs info</strong> hold a figure back; items marked{" "}
          <strong>needs a look</strong> let the figure through but should be checked before you file. Nothing here changes a number on your forms.
        </p>
        {analysis.issues.length === 0 && (
          <div className="all-clear">
            <p>Nothing left to check.</p>
            <button type="button" className="btn btn--primary" onClick={() => go("report")}>
              See your report
            </button>
          </div>
        )}
        <IssueGroup title="Needs info" tone="blocker" list={blockers} />
        <IssueGroup title="Needs a look" tone="review" list={reviews} />
        {infos.length > 0 && (
          <details className="more">
            <summary>Good to know ({infos.length})</summary>
            <ul className="issues">
              {infos.map((i) => (
                <ReviewItem key={key(i)} issue={i} />
              ))}
            </ul>
          </details>
        )}
        {analysis.issues.length > 0 && blockers.length === 0 && (
          <p className="good-text">Nothing is holding a figure back. Your report is ready to look over.</p>
        )}
      </Card>
    </div>
  );
}

const key = (i: Issue) => `${i.code}|${i.personId}|${i.refs.join()}`;

function IssueGroup({ title, tone, list }: { title: string; tone: string; list: Issue[] }) {
  if (!list.length) return null;
  return (
    <section className="issue-group">
      <h3 className={`sub-head sub-head--${tone}`}>
        {title} <span className="count">{list.length}</span>
      </h3>
      <ul className="issues">
        {list.map((i) => (
          <ReviewItem key={key(i)} issue={i} />
        ))}
      </ul>
    </section>
  );
}

function ReviewItem({ issue }: { issue: Issue }) {
  const { yf, go } = useOrganizer();
  const step = stepFor(issue);
  const who = issue.personId && yf.people.length > 1 ? personName(yf, issue.personId) : null;
  return (
    <li className={`issue issue--${issue.severity}`}>
      {who && <p className="issue-who">{who}</p>}
      <p className="issue-msg">{issue.message}</p>
      <p className="issue-next">{issue.nextAction}</p>
      <Resolver issue={issue} />
      {step !== "review" && (
        <button type="button" className="link-btn" onClick={() => go(step)}>
          Go to {STEP_NAME[step]} →
        </button>
      )}
    </li>
  );
}

/** Quick answers for issues that a single decision settles. Everything else is fixed on its step. */
function Resolver({ issue }: { issue: Issue }) {
  switch (issue.code) {
    case "CORRECTION_UNCONFIRMED":
    case "CORRECTION_STALE":
      return <ConfirmCorrection issue={issue} />;
    case "CORRECTED_FORM_UNLINKED":
      return <LinkCorrected issue={issue} />;
    case "DUPLICATE_INFO_RETURN":
      return <Duplicates issue={issue} />;
    case "BOX11_MISMATCH":
    case "INFO_RETURN_VARIANCE":
    case "FORM_1099K_VARIANCE":
      return <AcceptVariance issue={issue} />;
    case "INFO_RETURN_MATCHES_NET":
    case "INFO_RETURN_MATCHES_GROSS":
      return <FeeTreatment issue={issue} />;
    case "BASIS_CONFLICT":
    case "INFO_RETURN_UNLINKED":
      return <IncomeBasis issue={issue} />;
    case "YEAR_END_STRADDLE":
      return <RecognitionYear issue={issue} />;
    case "POSSIBLE_DUPLICATE_PAYOUT":
      return <SeparatePayouts issue={issue} />;
    case "TREATMENT_NEEDED":
      return <Treatment issue={issue} />;
    case "FEE_UNKNOWN":
      return <NoFee issue={issue} />;
    case "DECISION_STALE":
      return <StaleDecision issue={issue} />;
    default:
      return null;
  }
}

function Actions({ children }: { children: React.ReactNode }) {
  return <div className="resolve">{children}</div>;
}

function ConfirmCorrection({ issue }: { issue: Issue }) {
  const { yf, update } = useOrganizer();
  const c = yf.corrections.find((x) => x.id === issue.refs[0] && x.deletedAt === null);
  if (!c) return null;
  const original = yf.sources.find((s) => s.id === c.originalId);
  const replacement = c.replacementId ? yf.sources.find((s) => s.id === c.replacementId) : undefined;
  return (
    <Actions>
      <p className="resolve-q">
        {c.replacementId ? (
          <>
            Does <strong>{formLabel(replacement)}</strong> replace <strong>{formLabel(original)}</strong>?
          </>
        ) : (
          <>
            Was <strong>{formLabel(original)}</strong> voided by the payer?
          </>
        )}
      </p>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() =>
          update((y) =>
            reviseRecord(y, "corrections", c.id, {
              status: "confirmed",
              confirmedRevisions: { original: sourceRev(y, c.originalId), replacement: c.replacementId ? sourceRev(y, c.replacementId) : null },
            }),
          )
        }
      >
        Yes, confirm
      </button>
      <button type="button" className="btn btn--plain btn--sm" onClick={() => update((y) => reviseRecord(y, "corrections", c.id, { status: "rejected", confirmedRevisions: null }))}>
        No
      </button>
    </Actions>
  );
}

function sameScope(a: Source, b: Source): boolean {
  if (a.kind !== b.kind || a.personId !== b.personId || a.taxYear !== b.taxYear) return false;
  if (a.kind === "form_1099b" && b.kind === "form_1099b") return a.accountId === b.accountId && a.description.trim().toLowerCase() === b.description.trim().toLowerCase();
  return a.firmId === b.firmId;
}

function LinkCorrected({ issue }: { issue: Issue }) {
  const { yf, update } = useOrganizer();
  const f = yf.sources.find((s) => s.id === issue.refs[0] && s.deletedAt === null);
  const [target, setTarget] = useState("");
  if (!f) return null;
  const candidates = yf.sources.filter((s) => s.deletedAt === null && s.id !== f.id && sameScope(s, f));
  return (
    <Actions>
      {candidates.length > 0 && (
        <div className="resolve-row">
          <label className="resolve-q" htmlFor={`orig-${f.id}`}>
            It replaces
          </label>
          <select id={`orig-${f.id}`} value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose the original form</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {formLabel(c)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={!target}
            onClick={() =>
              update((y) =>
                addRecord(y, "corrections", {
                  id: newId(),
                  originalId: target,
                  replacementId: f.id,
                  status: "confirmed",
                  confirmedRevisions: { original: sourceRev(y, target), replacement: sourceRev(y, f.id) },
                }),
              )
            }
          >
            Link
          </button>
        </div>
      )}
      <button
        type="button"
        className="btn btn--outline btn--sm"
        onClick={() => update((y) => decide(y, { id: newId(), kind: "corrected_form_only", sourceId: f.id, sourceRevision: sourceRev(y, f.id) }, (d) => d.kind === "corrected_form_only" && d.sourceId === f.id))}
      >
        It's the only version I received
      </button>
    </Actions>
  );
}

function Duplicates({ issue }: { issue: Issue }) {
  const { yf, update } = useOrganizer();
  const forms = issue.refs.map((id) => yf.sources.find((s) => s.id === id && s.deletedAt === null)).filter((s): s is Source => Boolean(s));
  if (forms.length < 2) return null;
  const [a, b] = forms as [Source, Source];
  const corrects = (orig: Source, repl: Source) =>
    update((y) =>
      addRecord(y, "corrections", {
        id: newId(),
        originalId: orig.id,
        replacementId: repl.id,
        status: "confirmed",
        confirmedRevisions: { original: sourceRev(y, orig.id), replacement: sourceRev(y, repl.id) },
      }),
    );
  return (
    <Actions>
      <ul className="resolve-list">
        {forms.map((f) => (
          <li key={f.id}>
            {describe(f)}{" "}
            <button type="button" className="link-btn link-btn--danger" onClick={() => confirm("Remove this form? Use this when it's a copy of the other one.") && update((y) => removeRecord(y, "sources", f.id, nowIso()))}>
              Remove as a copy
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn btn--outline btn--sm"
        onClick={() =>
          update((y) =>
            decide(y, { id: newId(), kind: "confirm_both_info_returns", sourceIds: forms.map((f) => f.id), revisions: forms.map((f) => sourceRev(y, f.id)) }, (d) => d.kind === "confirm_both_info_returns" && forms.every((f) => d.sourceIds.includes(f.id))),
          )
        }
      >
        They're separate forms, count {forms.length === 2 ? "both" : "all of them"}
      </button>
      {forms.length === 2 && (
        <>
          <button type="button" className="btn btn--plain btn--sm" onClick={() => corrects(a, b)}>
            {describe(b)} is the newer, corrected one
          </button>
          <button type="button" className="btn btn--plain btn--sm" onClick={() => corrects(b, a)}>
            {describe(a)} is the newer, corrected one
          </button>
        </>
      )}
    </Actions>
  );
}

function AcceptVariance({ issue }: { issue: Issue }) {
  const { yf, analysis, update } = useOrganizer();
  const [reason, setReason] = useState("");
  const subject = issue.refs[0]!;
  let variance: string | null = null;
  let subjectRevision: number | undefined;
  if (issue.code === "BOX11_MISMATCH") {
    variance = analysis.reconciliation.futures.flatMap((r) => r.forms).find((f) => f.sourceId === subject)?.difference ?? null;
    subjectRevision = rev(yf.sources, subject);
  } else {
    const r = analysis.reconciliation.prop.find((x) => x.firmId === subject && x.personId === issue.personId);
    variance = (issue.code === "FORM_1099K_VARIANCE" ? r?.kVariance : r?.variance) ?? null;
    subjectRevision = rev(yf.firms, subject);
  }
  if (variance === null || subjectRevision === undefined) return null;
  const v = variance;
  const r = subjectRevision;
  return (
    <Actions>
      <TextInput label="If you know why, note it" value={reason} onChange={setReason} placeholder="For example: the firm included a December payout I received in January" />
      <button
        type="button"
        className="btn btn--outline btn--sm"
        disabled={!reason.trim()}
        onClick={() =>
          update((y) => decide(y, { id: newId(), kind: "accept_variance", subjectId: subject, subjectRevision: r, variance: v, reason: reason.trim() }, (d) => d.kind === "accept_variance" && d.subjectId === subject && new D(d.variance).equals(v)))
        }
      >
        Keep both figures as they are
      </button>
      <p className="field-hint">The form and your records stay unchanged. Your note goes in the report so you or your preparer can see why.</p>
    </Actions>
  );
}

function FeeTreatment({ issue }: { issue: Issue }) {
  const { update } = useOrganizer();
  const firmId = issue.refs[0]!;
  const personId = issue.personId!;
  const set = (treatment: "gross_income_plus_fee_expense" | "net_reported_no_fee_deduction") =>
    update((y) => decide(y, { id: newId(), kind: "fee_treatment", firmId, personId, treatment }, (d) => d.kind === "fee_treatment" && d.firmId === firmId && d.personId === personId));
  return (
    <Actions>
      <button type="button" className="btn btn--outline btn--sm" onClick={() => set("net_reported_no_fee_deduction")}>
        Report after fees, no fee deduction
      </button>
      <button type="button" className="btn btn--plain btn--sm" onClick={() => set("gross_income_plus_fee_expense")}>
        Report before fees and deduct the fees
      </button>
    </Actions>
  );
}

const BASES: { value: Extract<Decision, { kind: "prop_income_basis" }>["basis"]; label: string }[] = [
  { value: "manual", label: "The payouts I entered" },
  { value: "payout_records", label: "The firm's payout records" },
  { value: "processor_receipts", label: "Processor receipts" },
  { value: "info_return_total", label: "The 1099 total" },
];

function IncomeBasis({ issue }: { issue: Issue }) {
  const { update } = useOrganizer();
  const firmId = issue.refs[0]!;
  const personId = issue.personId!;
  const options = issue.code === "INFO_RETURN_UNLINKED" ? BASES.filter((b) => b.value === "info_return_total") : BASES;
  return (
    <Actions>
      <p className="resolve-q">{issue.code === "INFO_RETURN_UNLINKED" ? "Or count this firm's income from:" : "Count this firm's income from:"}</p>
      {options.map((b) => (
        <button
          key={b.value}
          type="button"
          className="btn btn--outline btn--sm"
          onClick={() => update((y) => decide(y, { id: newId(), kind: "prop_income_basis", firmId, personId, basis: b.value }, (d) => d.kind === "prop_income_basis" && d.firmId === firmId && d.personId === personId))}
        >
          {b.label}
        </button>
      ))}
    </Actions>
  );
}

function RecognitionYear({ issue }: { issue: Issue }) {
  const { yf, update } = useOrganizer();
  const e = yf.events.find((x) => x.id === issue.refs[0] && x.deletedAt === null);
  if (!e || e.kind !== "compensation") return null;
  const years = [...new Set(Object.values(e.dates).filter((d): d is string => d !== null).map((d) => Number(d.slice(0, 4))))].sort();
  return (
    <Actions>
      <p className="resolve-q">Which year could you first withdraw this money?</p>
      {years.map((yr) => (
        <button
          key={yr}
          type="button"
          className="btn btn--outline btn--sm"
          onClick={() =>
            update((y) =>
              decide(
                y,
                { id: newId(), kind: "recognition_year", eventId: e.id, eventRevision: eventRev(y, e.id), recognizeIn: yr, reason: `You confirmed the money was available to you in ${yr}.` },
                (d) => d.kind === "recognition_year" && d.eventId === e.id,
              ),
            )
          }
        >
          {yr}
        </button>
      ))}
    </Actions>
  );
}

function SeparatePayouts({ issue }: { issue: Issue }) {
  const { update } = useOrganizer();
  const [a, b] = issue.refs as [string, string];
  return (
    <Actions>
      <button
        type="button"
        className="btn btn--outline btn--sm"
        onClick={() =>
          update((y) =>
            decide(y, { id: newId(), kind: "dismiss_duplicate", a, aRevision: eventRev(y, a), b, bRevision: eventRev(y, b) }, (d) => d.kind === "dismiss_duplicate" && ((d.a === a && d.b === b) || (d.a === b && d.b === a))),
          )
        }
      >
        They're two separate payouts
      </button>
    </Actions>
  );
}

const TREATMENTS: { value: ExpenseTreatment; label: string }[] = [
  { value: "current", label: "Business expense" },
  { value: "startup", label: "Startup cost" },
  { value: "personal", label: "Personal" },
];

function Treatment({ issue }: { issue: Issue }) {
  const { update } = useOrganizer();
  const id = issue.refs[0]!;
  return (
    <Actions>
      {TREATMENTS.map((t) => (
        <button key={t.value} type="button" className="btn btn--outline btn--sm" onClick={() => update((y) => reviseRecord(y, "events", id, { treatment: t.value } as never))}>
          {t.label}
        </button>
      ))}
    </Actions>
  );
}

function NoFee({ issue }: { issue: Issue }) {
  const { update } = useOrganizer();
  const id = issue.refs[0]!;
  return (
    <Actions>
      <button type="button" className="btn btn--outline btn--sm" onClick={() => update((y) => reviseRecord(y, "events", id, { payoutFee: NOT_APPLICABLE } as never))}>
        There was no fee
      </button>
    </Actions>
  );
}

function StaleDecision({ issue }: { issue: Issue }) {
  const { update } = useOrganizer();
  return (
    <Actions>
      <button type="button" className="btn btn--outline btn--sm" onClick={() => update((y) => removeRecord(y, "decisions", issue.refs[0]!, nowIso()))}>
        Clear the old answer
      </button>
      <p className="field-hint">Any question it answered will come back here so you can answer it again for the changed record.</p>
    </Actions>
  );
}
